import { supabase } from '@/core/database/supabase';
import type { 
  QaInspection, 
  QaInspectionItem, 
  QaChecklist, 
  QaStats, 
  QaFilters 
} from '../types/maintenance-qa.types';

export const MaintenanceQaService = {
  /**
   * Lấy danh sách kiểm tra QA
   */
  async getInspections(companyId: string, filters: QaFilters = {}): Promise<{ data: QaInspection[], total: number }> {
    let query = supabase
      .from('qa_inspections')
      .select(`
        *,
        project:projects(id, name),
        work_order:work_orders(id, work_order_code, title),
        inspector:user_profiles!inspector_id(id, full_name, avatar_url:avatar_path),
        checklist:qa_checklists(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.inspectorId) query = query.eq('inspector_id', filters.inspectorId);
    if (filters.search) {
      query = query.or(`code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as QaInspection[],
      total: count || 0
    };
  },

  /**
   * Chi tiết đợt kiểm tra
   */
  async getInspectionDetail(id: string): Promise<QaInspection> {
    const { data, error } = await supabase
      .from('qa_inspections')
      .select(`
        *,
        project:projects(id, name),
        work_order:work_orders(id, work_order_code, title),
        inspector:user_profiles!inspector_id(id, full_name, avatar_url:avatar_path),
        checklist:qa_checklists(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as QaInspection;
  },

  /**
   * Lấy các hạng mục kiểm tra của một đợt
   */
  async getInspectionItems(inspectionId: string): Promise<QaInspectionItem[]> {
    const { data, error } = await supabase
      .from('qa_inspection_items')
      .select(`
        *,
        checklist_item:qa_checklist_items(id, name, description, weight)
      `)
      .eq('inspection_id', inspectionId);

    if (error) throw error;
    return data as QaInspectionItem[];
  },

  /**
   * Thống kê chất lượng
   */
  async getQaStats(companyId: string): Promise<QaStats> {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('qa_inspections')
      .select('status_code, final_result, overall_score, created_at')
      .eq('company_id', companyId);

    if (error) throw error;

    const todayItems = data.filter(i => i.created_at?.split('T')[0] === today);
    const passed = data.filter(i => i.final_result === 'pass').length;
    const scores = data.filter(i => i.overall_score !== undefined).map(i => i.overall_score!);

    return {
      pendingInspections: data.filter(i => i.status_code === 'pending_inspection').length,
      completedToday: todayItems.filter(i => i.status_code === 'passed' || i.status_code === 'failed').length,
      averageScore: scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
      passRate: data.length > 0 ? (passed / data.length) * 100 : 0,
      total_inspections: data.length,
      pending_inspection: data.filter(i => i.status_code === 'pending_inspection').length,
      first_time_pass: passed,
      failed: data.filter(i => i.final_result === 'fail').length,
      rework_required: data.filter(i => i.status_code === 'rework_required').length,
      reverified: data.filter(i => i.status_code === 'recheck_pending').length,
      average_score: scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
      pass_rate: data.length > 0 ? (passed / data.length) * 100 : 0,
    };
  },

  /**
   * Cập nhật kết quả kiểm tra
   */
  async submitResult(id: string, result: { score: number, result: string, notes?: string }): Promise<void> {
    const { error } = await supabase
      .from('qa_inspections')
      .update({
        overall_score: result.score,
        final_result: result.result,
        notes: result.notes,
        status_code: result.result === 'pass' ? 'passed' : 'failed',
        inspection_date: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * CRUD
   */
  async createInspection(data: Partial<QaInspection>): Promise<QaInspection> {
    const { data: result, error } = await supabase
      .from('qa_inspections')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as QaInspection;
  }
};

export const MaintenanceQAService = {
  ...MaintenanceQaService,
  getQAInspections: MaintenanceQaService.getInspections,
  getQAStats: MaintenanceQaService.getQaStats,
};
